import React, {
  useState, useContext, useRef, useEffect,
} from 'react';
import { useRouter } from 'next/router';
import {
  array, bool, func, string, arrayOf, shape,
  number, oneOfType,
} from 'prop-types';
import {
  Modal, Button, Form, Spin, Input, Select, Radio, DatePicker, Checkbox,
} from 'antd';
import moment, { lang } from 'moment';
import crypto from 'crypto-js';

import { getContactInfo } from 'actions/contact';
import FormInput from 'components/formInput';
import { NewsLetterHeaderSVG } from 'components/iconsSVG/newsletterHeader';
import { CloseMdSVG } from 'components/iconsSVG/close';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import { ThankYouMessage } from 'components/thankYouMessage';
import { useI18n } from 'context/i18n';
import { useUser } from 'context/user';
import { PhoneInput } from 'components/base';
import PrivacyPolicy from 'components/privacyPolicy';

import {
  getMarket, getLang, getCookie, COOKIES, getIsMobile, eraseCookie, CLOSE_SELLIGENT_POPUP,
} from 'utils/helpers';
import getConfigByLocale from 'utils/getConfigByLocale';

import {
  subscribeNewsletter, getInfo, updateNewsletter, getIsSelligent,
} from 'actions/general';

import { dataLayerHandleEvent } from 'utils/dataLayers';
import { IS_CAMPER, IS_NNORMAL } from 'utils/constants';
import { useValidate } from 'hooks';
import styles from './style.module.css';

const { SHA256 } = crypto;

const STEP = {
  SUBSCRIBE: 1,
  UPDATE: 2,
  THANKS: 3,
};

const defaultPictureURL = IS_NNORMAL ? '/assets-new/img/newsletter/nnormal-newsletter-default' : 'https://www.camper.com/img/BLF';

function NewsletterModal({
  visible, onClose, countries, nlOrigin, initialEmail, destroyOnClose, pageName,
  source, sendUsrHash, cmsProps, camperlab,
}) {
  const isMobile = getIsMobile('767.5px');
  const {
    t, localeConfigs, profileData, popupInfo, additionalAttributes, ...rest
  } = useI18n();
  const validate = useValidate();
  const { userData, readyForVouchers } = useUser();
  const router = useRouter();
  const { query } = router;

  // TEMPORAL HASTA QUE ARREGLEMOS EL FALLO EN _APP
  let { locale } = rest;

  locale = locale === 'catchAll' || locale === 'int' ? profileData.defaultProfile : locale;

  const { attentive } = additionalAttributes;

  const getLangsFromCountry = (country) => {
    const foundCountry = countries.find((item) => item.id === country);

    if (foundCountry === undefined) {
      return [];
    }

    return foundCountry.languages;
  };

  const { phonePrefixes } = profileData;
  const currentLanguage = getLang(locale);

  const [ alreadySeen, setAlreadySeen ] = useState(false);
  const [ cookieBondValue, setCookieBondValue ] = useState('');
  const [ filteredCountries, setFilteredCountries ] = useState(countries);
  const [ alreadySub, setAlreadySub ] = useState(false);
  const [ selectedCountry, setSelectedCountry ] = useState(getMarket(locale));
  const [ selectedLang, setSelectedLang ] = useState(currentLanguage);
  // const [ selectedPreffix, setSelectedPreffix ] = useState(null);
  const [ step, setStep ] = useState(STEP.SUBSCRIBE);
  const [ loading, setLoading ] = useState(true);
  const [ selligentUrl, setSelligentUrl ] = useState(null);
  const [ selligentBase, setSelligentBase ] = useState(null);
  const [ emailForm ] = Form.useForm();
  const [ AdditionalForm ] = Form.useForm();
  const [ filledEmail, setFilledEmail ] = useState(destroyOnClose);
  const [ isLoadingPreffixes, setIsLoadingPreffixes ] = useState(true);
  const [ prefixes, setPrefixes ] = useState([]);
  const [ loadingIframe, setLoadingIframe ] = useState(false);
  const [ localVisible, setLocalVisible ] = useState(visible);
  const [ showEmail, setShowEmail ] = useState(true);
  const [ dateValue, setDateValue ] = useState();
  const [ phoneValue, setPhoneValue ] = useState();

  const countryRef = useRef();
  const langRef = useRef();
  const market = getMarket(locale);
  const localeFormat = getConfigByLocale(locale, localeConfigs);

  // const getPreffixFromCountry = (countryId) => preffixes.filter((item) => item.id === countryId);

  const onCloseHandler = () => {
    if (localVisible === true) {
      setLocalVisible(false);
    }

    return onClose();
  };

  useEffect(() => {
    emailForm.resetFields();
    setFilledEmail(false);
  }, [ initialEmail ]);

  const callAttentiveTrigger = () => {
    if (window?.__attentive?.trigger) {
      window.__attentive.trigger();
      onClose();

      return true;
    }
    setTimeout(callAttentiveTrigger, 500);

    return false;
  };

  useEffect(() => {
    if (readyForVouchers) {
      const askPhoneCookie = getCookie(COOKIES.ASK_PHONE);

      if (askPhoneCookie === '1') {
        if (market !== 'US') {
          setLocalVisible(true);
          setShowEmail(false);
          eraseCookie(COOKIES.ASK_PHONE, COOKIES.DOMAIN);
        } else {
          // Quieren solo phone pero es US, tiramos trigger de attentive
          // controlar el tema trigger no esté cargado cuando debería

          if (attentive) {
            callAttentiveTrigger();
          }
        }
      }
    }
  }, [ readyForVouchers ]);

  useEffect(() => {
    if (visible) {
      const cookieBonds = getCookie(COOKIES.BONDS);
      setAlreadySeen(true);
      setCookieBondValue(cookieBonds);
      dataLayerHandleEvent({
        event: 'acquisition_popup',
        info_type: 'impression',
        campaign_detail: popupInfo?.title?.toLowerCase() || '',
        page_type: window.gtmType || '',
      });
    }
    if (!visible && alreadySeen && selligentUrl !== null) {
      const currentCookieValue = getCookie(COOKIES.BONDS);
      if (currentCookieValue !== cookieBondValue) {
        window.location.reload();
      }
    }
    setLocalVisible(visible);
  }, [ visible ]);

  useEffect(() => {
    if (window) {
      window.addEventListener('message', (event) => {
        const { data } = event;

        if (data === CLOSE_SELLIGENT_POPUP) {
          onClose();
        }
      });
    }

    const getSelligentHandler = async () => {
      const selligentResult = await getIsSelligent(locale);

      if (selligentResult) {
        const usrhashCookie = getCookie(COOKIES.SELLIGENT_USRHASH);
        let urlToSet = `${selligentResult}${camperlab ? '_camperlab' : ''}?lng=${currentLanguage}&country=${market.toLowerCase()}&isFromPopUp=true&device=${isMobile ? 'mobile' : 'desktop'}&source=${nlOrigin === 'promo' && pageName === 'PDP' ? 'pdp_message' : 'default'}`;

        if (usrhashCookie) {
          urlToSet = `${urlToSet}&usrhash=${usrhashCookie}`;
        }

        setSelligentBase(selligentResult);
        setSelligentUrl(urlToSet);
        setLoadingIframe(true);
        setLoading(false);
      } else if (selligentResult === false) { // no hay url selligent
        setLoading(false);
      }
    };

    getSelligentHandler();
  }, []);

  useEffect(() => {
    if (selligentBase !== null) {
      let protoSelligent = `${selligentBase}${camperlab ? '_camperlab' : ''}?lng=${currentLanguage}&country=${market.toLowerCase()}&isFromPopUp=true&device=${isMobile ? 'mobile' : 'desktop'}&source=${source === null ? 'default' : source}`;

      if (sendUsrHash) {
        const usrhashCookie = getCookie(COOKIES.SELLIGENT_USRHASH);

        if (usrhashCookie) {
          protoSelligent = `${protoSelligent}&usrhash=${usrhashCookie}`;
        }
      }

      setSelligentUrl(protoSelligent);
      setLoadingIframe(true);
    }
  }, [ source, sendUsrHash, isMobile ]);

  const handleChangeCountry = (country) => {
    setSelectedCountry(country);
    const langsInCountry = getLangsFromCountry(country);
    if (!langsInCountry.some(({ id }) => id === selectedLang)) {
      setSelectedLang(langsInCountry[0].id);
      AdditionalForm.setFieldsValue({ language: langsInCountry[0].id });
    }
  };

  const handleSearchCountry = (value) => {
    const result = countries.filter((item) => item.name.toLowerCase().includes(value.toLowerCase()));

    setFilteredCountries(result);
  };

  const onFinish = async (values) => {
    let emailToUse = values.email;
    let phonePrefix = null;

    const handleGetContactInfo = async () => {
      // lo sacamos en vivo para no guardarlo en ningún lado
      const { email, s } = query;
      const { contact } = await getContactInfo({
        context: null, lang: locale, email, s,
      });

      if (contact) {
        return contact.email;
      }

      return '';
    };

    setLoading(true);
    const isUpdateSub = step === STEP.UPDATE;

    // para tests email=YWdyYXVAY2FtcGVyLmNvbQ==&ap=R3JhdSBHYXZlbGE=&s=513dc17bcbb66a394b8e1475ee7ce557
    if (showEmail === false) {
      emailToUse = await handleGetContactInfo();
    }

    if (values?.preffix) {
      const realPrefix = phonePrefixes.find((item) => item.id === values.preffix);

      if (realPrefix) {
        [ phonePrefix ] = realPrefix.name.split('(').pop().split(')');
      }
    }

    if (phonePrefix === null) {
      // no ha tocado el prefijo
      const defaultPrefix = phonePrefixes.find((item) => item.id === market);

      if (defaultPrefix) {
        [ phonePrefix ] = defaultPrefix.name.split('(').pop().split(')');
      }
    }

    const payload = {
      email: emailToUse,
      telephone: values.telephone,
      phonePrefix,
      profile: {
        countryId: getMarket(locale),
        languageId: getLang(locale),
      },
      origin: nlOrigin,
    };

    if (isUpdateSub) {
      payload.birthDate = dateValue ? dateValue.format() : '';
      if (values.gender) {
        payload.gender = values.gender.target.value;
      }
      payload.countryId = selectedCountry;
      payload.languageId = selectedLang;
      payload.telephone = values.telephone || '';
      payload.origin = nlOrigin;
    }

    try {
      const result = !isUpdateSub ? await subscribeNewsletter(payload) : await updateNewsletter(payload);

      if (!isUpdateSub) {
        if (result.contactAlreadySubscribed) {
          if (showEmail === false) {
            // solo queremos el telefono
            setStep(STEP.THANKS);
          } else {
            setAlreadySub(true);
          }
        } else {
          setAlreadySub(false);

          if (attentive) {
            if (window?.__attentive?.trigger) {
              window.__attentive.trigger();
            }

            onClose();
          } else {
            setStep(STEP.UPDATE);
          }

          dataLayerHandleEvent({
            event: 'eventGA',
            eventAct: 'impression',
            eventCat: 'AcquisitionPopup',
            eventLbl: nlOrigin,
          });

          const eventLabelPopUp = window.dataLayer.find((key) => key.eventCat === 'AcquisitionPopup' && key.eventAct === 'impression')?.eventLbl;
          dataLayerHandleEvent({
            event: 'acquisition_popup',
            info_type: 'subscription',
            campaign_detail: popupInfo?.title?.toLowerCase() || '',
            page_type: window.gtmType || '',
          });

          dataLayerHandleEvent({
            event: 'eventGA',
            eventCat: 'AcquisitionPopup',
            eventAct: 'subscription',
            eventLbl: eventLabelPopUp,
          });
          dataLayerHandleEvent({
            event: 'eventGA',
            eventCat: 'NewsletterSubscription',
            eventLbl: nlOrigin,
            eventAct: getMarket(locale),
          });
          dataLayerHandleEvent({
            event: 'mailShadowed',
            mailSHA256: SHA256(values?.email?.toLowerCase()).toString(),
            mailVanilla: values?.email,
          });
        }
      } else {
        setStep(STEP.THANKS);
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleEmailChange = async () => {
    if (alreadySub) {
      setAlreadySub(false);
    }
    try {
      const value = await emailForm.validateFields([ 'email' ]);
      setFilledEmail(value.email);
      Promise.resolve();
    } catch (e) {
      setFilledEmail(false);
    }
  };

  let modalWidth = step === STEP.THANKS ? '355px' : '760px';
  let modalClass = step !== STEP.THANKS ? styles.modalWrapper : `${styles.modalWrapper} ${styles.modalWrapperThanks}`;

  if (selligentUrl !== null) {
    modalWidth = '550px';
    modalClass = `${modalClass} ${styles.selligentModal}`;
  }

  const handleLoadIframe = () => {
    setLoadingIframe(false);
  };

  const isAlreadySubscribed = userData?.account?.newsletterSubscribed === true;

  const dateForImg = new Date().toISOString().split('T')[0];

  const dateStringForImg = dateForImg?.replace(/-/g, '') || 'shouldnothappen';

  const modalTitle = showEmail === false ? t('newsletter', 'subscribed.title') : cmsProps?.newsletterPopup?.newsletterPopupClaim || popupInfo?.title || t('header.messages', `header.ribbon.01.${getMarket(locale)}.title`, 'Camper Newsletter');

  const Title = () => <p className={styles.title} dangerouslySetInnerHTML={{ __html: modalTitle }}></p>;

  const Subtitle = () => {
    const externalSubtitle = cmsProps?.newsletterPopup?.newsletterPopupSubclaim || popupInfo?.text;

    if (externalSubtitle && showEmail) {
      return <p className={styles.subtitle} dangerouslySetInnerHTML={{ __html: externalSubtitle }}></p>;
    }

    let internalSubtitle = null;

    if (showEmail === false) {
      internalSubtitle = t('newsletter', 'subscribed.subtitle');
    } else {
      internalSubtitle = alreadySub ?
        t('mi.cuenta', 'login.titulo.bienvenido', 'Good to see you again') :
        t('header.messages', `header.ribbon.01.${getMarket(locale)}.text`, 'Subscribe to our newsletter to receive an extra 10% off any product from our collection – just another perk of being part of the Camper family.');
    }

    return (<p className={styles.subtitle}
    dangerouslySetInnerHTML={{ __html: internalSubtitle }}
    ></p>);
  };

  const Icon = () => {
    const cmsIcon = cmsProps?.newsletterPopupImages?.newsletterIcon ?? false;

    if (cmsIcon) {
      const type = cmsIcon.tags.find((tag) => tag.includes('type'));
      return <img src={`${cmsIcon.url}${cmsIcon.fileName}?d=${dateStringForImg}`} type={type} alt="" />;
    }
    return null;
    // return <NewsLetterHeaderSVG />;
  };

  const Picture = () => {
    const pictures = cmsProps?.newsletterPopupImages?.newsletterImages;

    if (Array.isArray(pictures) && pictures.length > 0) {
      const jpgImage = pictures.find((picture) => picture.fileName.includes('jpg')) ?? pictures[0];
      return (
        <picture>
          {pictures.map((picture) => (
            <source
              key={picture.fileName}
              type={picture.tags[0]}
              srcSet={`${picture.url}${picture.fileName}?d=${dateStringForImg}`}
            />))
          }

          <img
            src={`${jpgImage.url}${jpgImage.fileName}?d=${dateStringForImg}`}
            alt={'Image of newsletter photo'}
            width={'auto'}
            height={'100%'}
          />
      </picture>
      );
    }

    return (
      <picture>
        <source
          type="image/avif"
          srcSet={`${defaultPictureURL}.avif?d=${dateStringForImg}`}
        />
        <source
          type="image/webp"
          srcSet={`https://www.camper.com/img/BLF.webp?d=${dateStringForImg}`}
        />
        <img
          src={`${defaultPictureURL}.jpg?d=${dateStringForImg}`}
          alt={'Image of newsletter photo'}
          width={'auto'}
          height={'100%'}
        />
      </picture>
    );
  };

  const handleChangePhone = (value) => {
    setPhoneValue(value);
  };

  return (
    <Modal
      title=""
      width={modalWidth}
      visible={localVisible}
      footer={null}
      onCancel={onCloseHandler}
      wrapClassName={modalClass}
      closable={false}
      destroyOnClose={destroyOnClose}
      zIndex={1000}
    >
      <Spin spinning={loading}>
        {
          selligentUrl === null ? <>
            <span onClick={onCloseHandler} className={styles.closeSvg}>
              <CloseMdSVG />
            </span>

            {step === STEP.SUBSCRIBE &&
              <div className={styles.modalContent}>
                <div className={styles.photo}>
                  <Picture />
                </div>
                <div className={styles.content}>
                  <div className={styles.svgWrapper}>
                    <Icon />
                  </div>
                  <Title />
                  <Subtitle />
                  <Form
                    form={emailForm}
                    layout="vertical"
                    name="emailForm"
                    onFinish={onFinish}
                    requiredMark='optional'
                    scrollToFirstError
                    className={styles.emailForm}
                  >
                    {
                      showEmail &&
                      <Form.Item
                        name="email"
                        rules={
                          [
                            {
                              required: true,
                              message: t('validation', 'required.email', 'Please input your email'),
                            },
                            {
                              type: 'string',
                              pattern: /^[-\w.%+]{1,64}@(?:[A-Z0-9-]{1,63}\.){1,125}[A-Z]{2,63}$/i,
                              message: '',
                            },
                          ]
                        }
                        required
                        initialValue={initialEmail}
                      >
                        <Input onChange={handleEmailChange} placeholder={t('generico', 'input.email.placeholder', 'Enter your email')} type="email" autoComplete="false"/>
                      </Form.Item>
                    }
                    { (market === 'IT' || showEmail === false) &&
                      <PhoneInput onChange={handleChangePhone} placeholder={t('generico', 'input.fecha.telefono', 'Phone')} className={styles.newsletterPhone} rules={validate('telephone')} info={{ phonePrefixes }} />
                    }
                    {
                      (filledEmail !== false || isAlreadySubscribed || (showEmail === false && phoneValue !== undefined)) && <>
                        <PrivacyPolicy origin='newsletter' hideConditions checkBoxClass={market !== 'TR' ? styles.privacyCheckbox : `${styles.privacyCheckbox} ${styles.withMargin}`} />
                        {
                          alreadySub &&
                            <span className="error">{t('newsletter', 'gracias.existe.mail')}</span>
                        }

                        <Form.Item className={styles.submitItem}>
                          <Button
                            type="default"
                            htmlType="submit"
                            className={styles.submitBtn}
                          >
                            {
                              t('generico', 'subscribe', 'Subscribe')
                            }
                          </Button>
                        </Form.Item>
                      </>
                    }

                  </Form>
                </div>
                {/* <div className={styles.footerPhoto}></div> */}
              </div>
            }

            {step === STEP.UPDATE &&
              <div className={styles.modalContentUpdate}>
                <div className={styles.contentPadded}>
                  <div className={styles.svgWrapper}>
                    <img src="/assets-new/newsletter-subscription-socks.gif" alt="Subscription OK!" width={90} height={90} />
                  </div>
                  <p className={styles.title}>{t('generico', IS_CAMPER ? 'camper.newsletter.agradecimiento' : 'newsletter.agradecimiento', 'Thank you for your subscription!')}</p>
                  <p className={`${styles.subtitle} ${styles.subtitleUpdate}`}>
                    {t('generico', IS_CAMPER ? 'camper.newsletter.mas.info.title' : 'newsletter.mas.info.title', 'Tell Us About Yourself.')}<br/>
                    {t('generico', IS_CAMPER ? 'camper.newsletter.mas.info.subtitle' : 'newsletter.mas.info.subtitle', 'It helps if we have a little bit of information about you.')}
                  </p>
                  <Form
                    form={AdditionalForm}
                    layout="vertical"
                    name="additionalForm"
                    onFinish={onFinish}
                    requiredMark=''
                    scrollToFirstError
                    className={styles.additionalForm}
                    initialValues={{
                      email: filledEmail,
                      country: getMarket(locale),
                      language: getLang(locale),
                      telephone: phoneValue,
                    }}
                  >
                    <Form.Item
                      label={t('generico', 'input.email', 'Email')}
                      name="email"
                      rules={validate('email')}
                      required
                      className={styles.emailInput}
                    >
                      <Input
                        placeholder={t('generico', 'input.email.placeholder', 'Enter your email')}
                        type="email"
                        disabled
                        formNoValidate
                        />
                    </Form.Item>
                    <Form.Item
                      name="telephone"
                      label={t('generico', 'input.fecha.telefono')}
                      className={styles.formPhone}
                      rules={
                        [
                          {
                            required: false,
                          },
                        ]
                      }
                    >
                      <div className={styles.phoneWrapper}>
                        <PhoneInput info={{ phonePrefixes }} placeholder={t('generico', 'input.fecha.telefono', 'Phone')} rules={validate('telephone')} className={styles.newsletterPhone} />
                      </div>
                    </Form.Item>

                    <FormInput
                      name="date"
                      label={t('generico', 'input.fecha.nacimiento', 'Date of birth')}
                      rules={[
                        {
                          required: false,
                        },
                      ]}
                    >
                      <div className={styles.datePicker}>
                        <DatePicker
                          locale={localeFormat}
                          format={localeFormat.dateFormat}
                          value={dateValue}
                          onChange={(newDate) => {
                            AdditionalForm.setFields([
                              {
                                name: 'date',
                                value: newDate,
                                errors: [],
                              },
                            ]);
                            setDateValue(newDate);
                          }}
                          onBlur={() => {
                            // getFieldValue aquí nos devuelve undefined (o moment object)
                            // mientras que en myprofile nos devuelve una string
                            const realValue = AdditionalForm.getFieldValue('date');

                            if (realValue !== undefined) {
                              const formattedValue = moment(realValue, localeFormat.dateFormat);

                              if (formattedValue._isValid) {
                                AdditionalForm.setFields([
                                  {
                                    name: 'date',
                                    errors: [],
                                  },
                                ]);
                                setDateValue(formattedValue);
                              } else {
                                if (realValue) {
                                  AdditionalForm.setFields([
                                    {
                                      name: 'date',
                                      errors: [ t('newsletter', 'error.fecha.incorrecta.registro') ],
                                    },
                                  ]);
                                }
                                setDateValue('');
                              }
                            }
                          }}
                        />
                      </div>
                    </FormInput>

                    <Form.Item name="gender" className={styles.genderSelector} valuePropName="" label={t('newsletter', 'genero')}>
                      <Radio.Group>
                        <Radio value="W">{t('generico', 'input.mujer', 'Women')}</Radio>
                        <Radio value="M">{t('generico', 'input.hombre', 'Men')}</Radio>
                      </Radio.Group>
                    </Form.Item>

                    <FormInput
                      name="country"
                      className={styles.selector}
                      rules={validate('requiredCountry')}
                      label={t('newsletter', 'pais', '**Country of residence')}
                    >
                      <Select
                        ref={countryRef}
                        showSearch
                        value={selectedCountry}
                        onSearch={handleSearchCountry}
                        filterOption={false}
                        onChange={(val) => {
                          countryRef.current.blur();
                          handleChangeCountry(val);
                        }}
                        placeholder={t('modal.idiomas', 'select.country', 'Select a country')}
                        key={selectedCountry}
                        className={styles.modalSelect}
                        suffixIcon ={<ChevronSVG />}
                      >
                        {filteredCountries.map((country) => (
                          <Select.Option key={country.id} className={styles.selectItem}>
                            <p>{country.name}</p>
                          </Select.Option>
                        ))}
                      </Select>
                    </FormInput>
                    <FormInput
                      name="language"
                      className={styles.selector}
                      rules={validate('requiredLanguage')}
                      label={t('newsletter', 'idioma', '**Language')}
                    >
                      <Select
                        ref={langRef}
                        disabled={!selectedCountry}
                        autoComplete='false'
                        onChange={(val) => {
                          langRef.current.blur();
                          setSelectedLang(val);
                        }}
                        placeholder={t('modal.idiomas', 'select.language', 'Select a language')}
                        className={styles.modalSelect}
                        suffixIcon ={<ChevronSVG />}
                        options={getLangsFromCountry(selectedCountry).map((language) => ({
                          value: language.id,
                          label: language.name,
                          className: `${styles.selectItem}`,
                        }))}
                      >
                        {getLangsFromCountry(selectedCountry).map((language) => (
                          <Select.Option key={language.id} value={language.id} className={styles.selectItem}>
                            <p>{language.name}</p>
                          </Select.Option>
                        ))}
                      </Select>
                    </FormInput>

                    <Form.Item className={styles.updateItem}>
                      <Button
                        type="default"
                        htmlType="submit"
                        className={styles.updateBtn}
                      >
                        {t('generico', 'boton.actualizar', 'Update')}
                      </Button>
                    </Form.Item>
                    <p onClick={() => setStep(STEP.THANKS)} className={styles.cancel}>{t('generico', 'boton.cancelar', 'Cancel')}</p>
                  </Form>
                </div>
              </div>
            }

            {step === STEP.THANKS &&
              <ThankYouMessage type='newsletter' className={styles.modalContentThanks} />
            }
          </> : <>
          <Spin spinning={loadingIframe}>
            <iframe id="selligent" onLoad={handleLoadIframe} className={styles.selligentIframe} src={`${selligentUrl}`}>
            </iframe>
            <span className={styles.closeButton} onClick={onCloseHandler}>
              <CloseMdSVG closeBgColor={popupInfo?.closeBgColor} />
            </span>
          </Spin>
          </>

        }
      </Spin>
    </Modal>
  );
}

NewsletterModal.defaultProps = {
  visible: false,
  onClose: () => false,
  countries: [],
  nlOrigin: '',
  initialEmail: '',
  destroyOnClose: false,
  pageName: '',
  source: null,
  sendUsrHash: false,
  cmsProps: {},
};

NewsletterModal.propTypes = {
  visible: bool,
  onClose: func,
  countries: array,
  nlOrigin: string,
  initialEmail: string,
  destroyOnClose: bool,
  pageName: string,
  source: string,
  sendUsrHash: bool,
  cmsProps: shape({
    newsletterPopup: shape({
      newsletterPopupClaim: string,
      newsletterPopupSubclaim: string,
    }),
    newsletterContent: shape({
      description: string,
      placeholder: string,
      title: string,
    }),
    newsletterPopupImages: shape({
      newsletterImages: arrayOf(shape({
        fileName: string,
        url: string,
        tags: arrayOf(string),
        metadata: shape({
          pixelWidth: number,
          pixelHeight: number,
          imageQuality: number,
          description: string,
        }),
      })),
      newsletterIcon: oneOfType([ shape({
        fileName: string,
        url: string,
        tags: arrayOf(string),
      }), string ]),
    }),
  }),
  camperlab: bool,
};

export { NewsletterModal };

export default NewsletterModal;
